import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { useLanguage } from '../../../context/LanguageContext';
import { updateStoreCustomerProfile, fetchStoreCustomerProfile } from '../../../firebase/customer-profile-functions';
import CustomerInfoForm from '../../../components/common/CustomerInfoForm/CustomerInfoForm';
import './UserAccountPage.css';
import AuthPage from "../auth/AuthPage";
import "../auth/AuthPage.css";

const UserAccount = () => {
    const { currentUser, logout, error, activeStoreId } = useUser();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [authOpen, setAuthOpen] = useState(false);

    // Translations for user account page
    const translations = {
        en: {
            myAccount: "My Account",
            login: "Login",
            logout: "Logout",
            pleaseLogin: "Please log in to view your account",
            profile: "Profile",
            orders: "Orders",
            wishlist: "Wishlist",
            personalInformation: "Personal Information",
            edit: "Edit",
            cancel: "Cancel",
            fullName: "Full Name",
            email: "Email",
            phoneNumber: "Phone Number",
            addressInformation: "Address Information",
            address: "Address",
            city: "City",
            stateEmirate: "State/Emirate",

            country: "Country",
            notProvided: "Not provided",
            emailCannotChange: "Email cannot be changed",
            saving: "Saving...",
            saveChanges: "Save Changes",
            profileUpdated: "Profile updated successfully!",
            countries: {
                UAE: "United Arab Emirates",
                KSA: "Saudi Arabia",
                QAT: "Qatar",
                KWT: "Kuwait",
                BHR: "Bahrain",
                OMN: "Oman"
            }
        },
        ar: {
            myAccount: "حسابي",
            login: "تسجيل الدخول",
            logout: "تسجيل الخروج",
            pleaseLogin: "يرجى تسجيل الدخول لعرض حسابك",
            profile: "الملف الشخصي",
            orders: "الطلبات",
            wishlist: "قائمة الأمنيات",
            personalInformation: "المعلومات الشخصية",
            edit: "تعديل",
            cancel: "إلغاء",
            fullName: "الاسم الكامل",
            email: "البريد الإلكتروني",
            phoneNumber: "رقم الهاتف",
            addressInformation: "معلومات العنوان",
            address: "العنوان",
            city: "المدينة",
            stateEmirate: "الولاية/الإمارة",
            postalCode: "الرمز البريدي",
            country: "البلد",
            notProvided: "غير مُدرج",
            emailCannotChange: "لا يمكن تغيير البريد الإلكتروني",
            saving: "جاري الحفظ...",
            saveChanges: "حفظ التغييرات",
            profileUpdated: "تم تحديث الملف الشخصي بنجاح!",
            countries: {
                UAE: "دولة الإمارات العربية المتحدة",
                KSA: "المملكة العربية السعودية",
                QAT: "قطر",
                KWT: "الكويت",
                BHR: "البحرين",
                OMN: "عُمان"
            }
        }
    };

    const t = translations[language] || translations.en;

    const [storeCustomerProfile, setStoreCustomerProfile] = useState(null);
    const [formData, setFormData] = useState({
        displayName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'UAE'
    });

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    // Fetch customer profile from the active store
    useEffect(() => {
        const fetchStoreCustomerProfileData = async () => {
            if (!currentUser || !activeStoreId) {
                setLoading(false);
                return;
            }

            try {
                const customerData = await fetchStoreCustomerProfile(currentUser.uid, activeStoreId);

                if (customerData) {
                    setStoreCustomerProfile(customerData);

                    // Initialize form data from store-specific customer profile using new structure
                    const customerInfo = customerData.customerInfo || {};
                    setFormData({
                        displayName: customerInfo.firstName && customerInfo.lastName
                            ? `${customerInfo.firstName} ${customerInfo.lastName}`
                            : '',
                        email: customerInfo.email || currentUser.email || '',
                        phone: customerInfo.phone || '',
                        address: customerInfo.address || '',
                        city: customerInfo.city || '',
                        state: customerInfo.state || '',
                        zipCode: customerInfo.zipCode || '',
                        country: customerInfo.country || 'UAE'
                    });
                } else {
                    // If no profile exists for this store, initialize with minimal data
                    setFormData({
                        displayName: currentUser.displayName || '',
                        email: currentUser.email || '',
                        phone: currentUser.phoneNumber || '',
                        address: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        country: 'UAE'
                    });
                }
            } catch (err) {
                console.error("Error fetching store customer profile:", err);
                setMessage("Error loading your profile data");
            } finally {
                setLoading(false);
            }
        };

        fetchStoreCustomerProfileData();
    }, [currentUser, activeStoreId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCancel = () => {
        // Reset form data to original values
        if (storeCustomerProfile) {
            const customerInfo = storeCustomerProfile.customerInfo || {};
            setFormData({
                displayName: customerInfo.firstName && customerInfo.lastName
                    ? `${customerInfo.firstName} ${customerInfo.lastName}`
                    : '',
                email: customerInfo.email || currentUser.email || '',
                phone: customerInfo.phone || '',
                address: customerInfo.address || '',
                city: customerInfo.city || '',
                state: customerInfo.state || '',
                zipCode: customerInfo.zipCode || '',
                country: customerInfo.country || 'UAE'
            });
        } else {
            // If no profile exists, reset to user data
            setFormData({
                displayName: currentUser.displayName || '',
                email: currentUser.email || '',
                phone: currentUser.phoneNumber || '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
                country: 'UAE'
            });
        }
        setIsEditing(false);
        setMessage(''); // Clear any messages
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            if (!currentUser || !activeStoreId) {
                throw new Error("User not logged in or store not selected");
            }

            // Parse display name to get first and last name
            const nameParts = formData.displayName.trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            // Prepare profile data in the order format
            const profileData = {
                firstName,
                lastName,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                zipCode: formData.zipCode,
                country: formData.country
            };

            // Update profile using the new function
            const updatedProfile = await updateStoreCustomerProfile(currentUser.uid, activeStoreId, profileData);

            if (updatedProfile) {
                setStoreCustomerProfile(updatedProfile);
                setMessage('Profile updated successfully!');
                setIsEditing(false);
            }
        } catch (err) {
            console.error("Error updating store customer profile:", err);
            setMessage(err.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (err) {
            setMessage(err.message || 'Failed to logout');
        }
    };

    if (loading) {
        return <div className="loading-container"><div className="loading-spinner"></div></div>;
    }

    if (!currentUser || !activeStoreId) {
        return (
            <div className="user-account-container">
                <div className="account-header">
                    <h1>{t.myAccount}</h1>
                </div>
                <div className="error-message">
                    <p>{t.pleaseLogin}</p>
                    <button onClick={() => setAuthOpen(true)} className="login-btn">{t.login}</button>

                    {authOpen && (
                        <div className="auth-overlay">
                            <div className="auth-popup">
                                <AuthPage panelMode={true} onClose={() => setAuthOpen(false)} />
                            </div>
                        </div>
                    )}


                </div>
            </div>
        );
    }


    return (
        <div className="user-account-container">
            <div className="account-header">
                <h1>{t.myAccount}</h1>
            </div>

            {message && <div className={message.includes('success') ? 'success-message' : 'error-message'}>{message}</div>}

            <div className="account-content">
                <div className="account-sidebar">
                    <div className="user-info">
                        <div className="user-avatar">
                            {formData.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                            <h3>{formData.displayName || 'User'}</h3>
                            <p>{formData.email}</p>
                        </div>
                    </div>

                    <ul className="account-navigation">
                        <li className="active">{t.profile}</li>
                        <li onClick={() => navigate('/orders')}>{t.orders}</li>
                        <li onClick={() => navigate('/wishlist')}>{t.wishlist}</li>
                        <li onClick={handleLogout}>{t.logout}</li>
                    </ul>
                </div>

                <div className="account-main">
                    <div className="account-section">
                        <form onSubmit={handleSubmit}>
                            <div className="section-header">
                                <h2>{t.personalInformation}</h2>
                                {!isEditing ? (
                                    <button type="button" className="edit-btn" onClick={() => {
                                        setIsEditing(true);
                                        setMessage(''); // Clear any messages when starting to edit
                                    }}>{t.edit}</button>
                                ) : (
                                    <button type="button" className="cancel-btn" onClick={handleCancel}>{t.cancel}</button>
                                )}
                            </div>

                            {!isEditing ? (
                                // Display mode
                                <div className="profile-display">
                                    <div className="form-group">
                                        <label>{t.fullName}</label>
                                        <p>{formData.displayName || t.notProvided}</p>
                                    </div>

                                    <div className="form-group">
                                        <label>{t.email}</label>
                                        <p>{formData.email}</p>
                                        <small>{t.emailCannotChange}</small>
                                    </div>

                                    <div className="form-group">
                                        <label>{t.phoneNumber}</label>
                                        <p>{formData.phone || t.notProvided}</p>
                                    </div>

                                    <div className="form-group">
                                        <label>{t.address}</label>
                                        <p>{formData.address || t.notProvided}</p>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>{t.city}</label>
                                            <p>{formData.city || t.notProvided}</p>
                                        </div>

                                        <div className="form-group">
                                            <label>{t.stateEmirate}</label>
                                            <p>{formData.state || t.notProvided}</p>
                                        </div>
                                    </div>

                                    <div className="form-row">


                                        <div className="form-group">
                                            <label>{t.country}</label>
                                            <p>{t.countries[formData.country] || formData.country || t.notProvided}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Edit mode using the unified component
                                <CustomerInfoForm
                                    formData={formData}
                                    setFormData={setFormData}
                                    storeSettings={{
                                        availableCountries: [
                                            { code: 'UAE', name: 'United Arab Emirates' },
                                            { code: 'KSA', name: 'Saudi Arabia' },
                                            { code: 'QAT', name: 'Qatar' },
                                            { code: 'KWT', name: 'Kuwait' },
                                            { code: 'BHR', name: 'Bahrain' },
                                            { code: 'OMN', name: 'Oman' }
                                        ]
                                    }}
                                    processing={loading}
                                    showCreateAccount={false}
                                    showUseExistingInfo={false}
                                    showShippingToggle={false}
                                    mode="profile"
                                />
                            )}

                            {/* Save Button */}
                            {isEditing && (
                                <div className="form-actions">
                                    <button type="submit" className="save-btn" disabled={loading}>
                                        {loading ? t.saving : t.saveChanges}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserAccount;